// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { getBrandDomainByHostname, getRuntimeBrandDomain } from './domain-brand';
import { AppName, BuildEnv, NodeEnv } from './types';

/**
 * Centralized runtime configuration from `process.env`.
 *
 * @remarks
 * - Use {@link environment} for grouped values (API URLs, Auth0, feature flags).
 * - Use {@link readEnv} when reading ad-hoc keys with the same parsing rules.
 * - Getters re-evaluate on each access so tests can mutate `process.env` without
 *   reloading the module.
 */

/**
 * Reads a single `process.env` entry as a trimmed string.
 *
 * @param key - Environment variable name.
 * @returns Trimmed non-empty string, or `undefined` if missing or blank.
 * @internal
 */
const rawEnv = <T extends keyof typeof process.env>(key: T): string | undefined => {
  const value = process.env[key];

  if (value === undefined) return undefined;

  const trimmed = value.trim();

  return trimmed === '' ? undefined : trimmed;
};

/**
 * Parses `process.env[key]` as a finite number.
 *
 * @param key - Environment variable name.
 * @returns Parsed number, or `undefined` if missing or not finite.
 * @internal
 */
const parseNumber = <T extends keyof typeof process.env>(
  key: T,
): number | undefined => {
  const value = rawEnv(key);

  if (value === undefined) return undefined;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Parses `process.env[key]` as a boolean.
 *
 * @param key - Environment variable name.
 * @returns `true` for `true` / `1` / `yes` (case-insensitive), `false` for `false`
 *   / `0` / `no`, or `undefined` if missing or unrecognized.
 * @internal
 */
const parseBoolean = <T extends keyof typeof process.env>(
  key: T,
): boolean | undefined => {
  const value = rawEnv(key)?.toLowerCase();

  if (value === undefined) return undefined;

  if (['true', '1', 'yes'].includes(value)) return true;

  if (['false', '0', 'no'].includes(value)) return false;

  return undefined;
};

/**
 * Parses `process.env[key]` as one of a fixed set of string literals.
 *
 * @param key - Environment variable name.
 * @param acceptedValues - Allowed string values (e.g. `Object.values(MyEnum)`).
 * @returns The matched literal, or `undefined` if missing or not in the set.
 * @internal
 */
const parseEnum = <
  T extends keyof typeof process.env,
  const AcceptedValues extends readonly string[],
>(
  key: T,
  acceptedValues: AcceptedValues,
): AcceptedValues[number] | undefined => {
  const value = rawEnv(key);

  if (value === undefined) return undefined;

  return acceptedValues.includes(value)
    ? (value as AcceptedValues[number])
    : undefined;
};

/**
 * Reads and optionally parses an environment variable.
 *
 * @example
 *
 * ```ts
 * const url = readEnv('NX_PUBLIC_API_ENGINE_BASE_URL');
 * const max = readEnv('NX_PUBLIC_FEATURE_AI_MAX_STOCKS', 'number');
 * const app = readEnv('NX_APP_NAME', { enum: Object.values(AppName) });
 * ```
 *
 * @param key - `process.env` key name.
 * @param kind - Optional parse mode: `'string'` (default), `'number'`, `'boolean'`,
 *   or `{ enum: readonly string[] }` for allow-list validation.
 * @returns Parsed value, or `undefined` when missing, blank, or invalid. Never
 *   throws; use non-null assertions or guards where the runtime guarantees a
 *   value.
 */
export function readEnv<T extends keyof typeof process.env>(
  key: T,
  kind: 'number',
): number | undefined;
export function readEnv<T extends keyof typeof process.env>(
  key: T,
  kind: 'boolean',
): boolean | undefined;
export function readEnv<
  T extends keyof typeof process.env,
  const AcceptedValues extends readonly string[],
>(
  key: T,
  kind: { readonly enum: AcceptedValues },
): AcceptedValues[number] | undefined;
export function readEnv<T extends keyof typeof process.env>(
  key: T,
  kind?: 'string',
): string | undefined;
export function readEnv<T extends keyof typeof process.env>(
  key: T,
  kind?: 'string' | 'number' | 'boolean' | { readonly enum: readonly string[] },
): string | number | boolean | undefined {
  if (kind === 'number') return parseNumber(key);

  if (kind === 'boolean') return parseBoolean(key);

  if (kind && typeof kind === 'object' && 'enum' in kind) {
    return parseEnum(key, kind.enum);
  }

  return rawEnv(key);
}

/**
 * Grouped configuration for apps and shared libraries.
 *
 * @remarks
 * Top-level and nested fields are **getters**: each access re-reads `process.env`
 * through {@link readEnv}. Nested objects are recreated per parent access.
 */
export const environment = {
  /**
   * Nx / CI application identity and build stamp.
   *
   * @remarks
   * Variables: `NX_APP_NAME`, `NX_APP_VERSION`, `NX_BUILD_ID`.
   */
  get APP() {
    return {
      /**
       * Nx application id (`@citec-platform/advisor-app` or
       * `@citec-platform/client-app`).
       *
       * @remarks
       * Env: `NX_APP_NAME`. Validated against {@link AppName}.
       */
      get NAME() {
        return process.env['NX_APP_NAME'];
      },
      /**
       * Semantic version string for display and persistence keys.
       *
       * @remarks
       * Env: `NX_APP_VERSION`.
       */
      get VERSION() {
        return process.env['NX_APP_VERSION'];
      },
      /**
       * Monotonic build id (number) for persist migrations and cache busting.
       *
       * @remarks
       * Env: `NX_BUILD_ID`.
       */
      get BUILD_ID() {
        return Number(process.env['NX_BUILD_ID']);
      },
    };
  },

  /**
   * Product brand for the current hostname (`citec` or `nuvara`).
   *
   * @remarks
   * Resolved from {@link getBrandDomainByHostname} using `window.location`. Nuvara
   * hosts match {@link NUVARA_HOSTNAMES} (same set as
   * `apps/client-app/src/index.html`).
   */
  get BRAND_DOMAIN() {
    return getRuntimeBrandDomain();
  },

  /**
   * HTTP base URLs for backend services.
   *
   * @remarks
   * Variables: `NX_PUBLIC_API_*`.
   */
  get API() {
    return {
      /**
       * Engine (core) API origin.
       *
       * @remarks
       * Env: `NX_PUBLIC_API_ENGINE_BASE_URL`.
       */
      get ENGINE_BASE_URL() {
        return readEnv('NX_PUBLIC_API_ENGINE_BASE_URL');
      },
      /**
       * Brokerage API origin.
       *
       * @remarks
       * Env: `NX_PUBLIC_API_BROKER_BASE_URL`.
       */
      get BROKER_BASE_URL() {
        return readEnv('NX_PUBLIC_API_BROKER_BASE_URL');
      },
      /**
       * AI / analytics API origin.
       *
       * @remarks
       * Env: `NX_PUBLIC_API_AI_BASE_URL`.
       */
      get AI_BASE_URL() {
        return readEnv('NX_PUBLIC_API_AI_BASE_URL');
      },
      /**
       * Copilot API origin (citec-app only; optional elsewhere).
       *
       * @remarks
       * Env: `NX_PUBLIC_API_COPILOT_BASE_URL`.
       */
      get COPILOT_BASE_URL() {
        return readEnv('NX_PUBLIC_API_COPILOT_BASE_URL');
      },
    };
  },

  /**
   * Shared HTTP defaults for API clients (not sourced from env except implicitly
   * via app code).
   */
  get API_CONSTANTS() {
    return {
      /** Default request timeout in milliseconds. */
      get TIMEOUT_MS() {
        return 80000 as const;
      },
      /** Default JSON request headers. */
      get HEADERS() {
        return {
          /** `Content-Type` header value for JSON bodies. */
          get CONTENT_TYPE() {
            return 'application/json' as const;
          },
          /** `Accept` header value for JSON responses. */
          get ACCEPT() {
            return 'application/json' as const;
          },
        };
      },
    };
  },

  /**
   * Auth0 settings with runtime `CLIENT_ID` resolution by hostname.
   *
   * @remarks
   * Shared tenant config:
   *
   * - `NX_PUBLIC_AUTH_DOMAIN`
   * - `NX_PUBLIC_AUTH_AUDIENCE`
   *
   * Client ids:
   *
   * - Citec domains -> `NX_PUBLIC_AUTH_CLIENT_ID`
   * - Nuvara domains (`advisor.nuvara360.com`, `app.nuvara360.com`,
   *   `dev-advisor.nuvara360.com`, `dev-app.nuvara360.com`) ->
   *   `NX_PUBLIC_AUTH_NUVARA_CLIENT_ID`
   */
  get AUTH() {
    const authScope = 'openid profile email read:current_user offline_access';

    const resolveClientId = (): string | undefined => {
      if (typeof window === 'undefined') return readEnv('NX_PUBLIC_AUTH_CLIENT_ID');

      return getBrandDomainByHostname(window.location.hostname) === 'nuvara'
        ? readEnv('NX_PUBLIC_AUTH_NUVARA_CLIENT_ID')
        : readEnv('NX_PUBLIC_AUTH_CLIENT_ID');
    };

    return {
      /**
       * @remarks
       * Env: `NX_PUBLIC_AUTH_DOMAIN`.
       */
      get DOMAIN() {
        return readEnv('NX_PUBLIC_AUTH_DOMAIN');
      },
      /**
       * @remarks
       * Determined by hostname:
       *
       * - Nuvara hostnames: `NX_PUBLIC_AUTH_NUVARA_CLIENT_ID`
       * - Other hostnames: `NX_PUBLIC_AUTH_CLIENT_ID`
       */
      get CLIENT_ID() {
        return resolveClientId();
      },
      /**
       * @remarks
       * Env: `NX_PUBLIC_AUTH_AUDIENCE`.
       */
      get AUDIENCE() {
        return readEnv('NX_PUBLIC_AUTH_AUDIENCE');
      },
      /** OAuth2 scopes string (shared constant). */
      get SCOPE() {
        return authScope;
      },
    };
  },

  /**
   * Broker OAuth2 configuration (primarily citec-app).
   *
   * @remarks
   * Client ids and redirect URL come from `NX_PUBLIC_BROKER_*` variables.
   */
  get BROKERS() {
    return {
      /** Tradier OAuth settings. */
      get TRADIER() {
        return {
          /**
           * @remarks
           * Env: `NX_PUBLIC_BROKER_TRADIER_CLIENT_ID`.
           */
          get CLIENT_ID() {
            return readEnv('NX_PUBLIC_BROKER_TRADIER_CLIENT_ID');
          },
          /** Authorization endpoint URL (fixed). */
          get OAUTH_URL() {
            return 'https://api.tradier.com/v1/oauth/authorize' as const;
          },
          /** Space-separated OAuth scopes (fixed). */
          get SCOPE() {
            return 'write,trade,market' as const;
          },
        };
      },
      /** Alpaca OAuth settings. */
      get ALPACA() {
        return {
          /**
           * @remarks
           * Env: `NX_PUBLIC_BROKER_ALPACA_CLIENT_ID`.
           */
          get CLIENT_ID() {
            return readEnv('NX_PUBLIC_BROKER_ALPACA_CLIENT_ID');
          },
          /**
           * Registered redirect URI for the Alpaca OAuth app. @remarks Env:
           * `NX_PUBLIC_BROKER_ALPACA_REDIRECT_URL`.
           */
          get REDIRECT_URL() {
            return readEnv('NX_PUBLIC_BROKER_ALPACA_REDIRECT_URL');
          },
          /** Authorization endpoint URL (fixed). */
          get OAUTH_URL() {
            return 'https://app.alpaca.markets/oauth/authorize' as const;
          },
          /** Space-separated OAuth scopes (fixed). */
          get SCOPE() {
            return 'account:write trading' as const;
          },
        };
      },
      /** Interactive Brokers OAuth step-one URL (fixed). */
      get INTERACTIVE_BROKERS() {
        return {
          /** Authorization endpoint URL (fixed). */
          get OAUTH_URL() {
            return 'https://www.interactivebrokers.com/authorize' as const;
          },
        };
      },
    };
  },

  /**
   * Third-party widgets, deep links, and static asset helpers.
   *
   * @remarks
   * Variables: `NX_PUBLIC_SERVICE_*`.
   */
  get SERVICES() {
    return {
      /**
       * HubSpot chat portal id (citec-app). @remarks Env:
       * `NX_PUBLIC_SERVICE_HUBSPOT_CHAT_ID`.
       */
      get HUBSPOT_CHAT_ID() {
        return readEnv('NX_PUBLIC_SERVICE_HUBSPOT_CHAT_ID');
      },

      /** Google Tag Manager container id. @remarks Env: `NX_PUBLIC_SERVICE_GTM_ID`. */
      get GOOGLE_TAG_MANAGER_ID() {
        return readEnv('NX_PUBLIC_SERVICE_GTM_ID');
      },

      /**
       * Retail client app base URL for links / QR payloads.
       *
       * @remarks
       * Citec/default: `NX_PUBLIC_SERVICE_CLIENT_APP_URL`. Nuvara hostnames:
       * `NX_PUBLIC_SERVICE_CLIENT_APP_URL_NUVARA`.
       */
      get CLIENT_APP_URL() {
        if (typeof window === 'undefined') {
          return readEnv('NX_PUBLIC_SERVICE_CLIENT_APP_URL');
        }

        return getBrandDomainByHostname(window.location.hostname) === 'nuvara'
          ? readEnv('NX_PUBLIC_SERVICE_CLIENT_APP_URL_NUVARA')
          : readEnv('NX_PUBLIC_SERVICE_CLIENT_APP_URL');
      },

      /**
       * Wealth Reader OAuth redirect URI (retail-app).
       *
       * @remarks
       * Computed dynamically as `${window.location.origin}/callback/wealth_reader`.
       * No environment variable is required.
       */
      get WEALTH_READER_REDIRECT_URL() {
        if (typeof window === 'undefined') return undefined;

        return `${window.location.origin}/callback/wealth_reader`;
      },

      /** Defaults for client-side QR code image URLs. */
      get QR_CODE() {
        return {
          /** External QR SVG/PNG API base (fixed). */
          get BASE_URL() {
            return 'https://api.qrserver.com/v1/create-qr-code/' as const;
          },
          /** Default pixel size token, e.g. `150x150` (fixed). */
          get DEFAULT_SIZE() {
            return '150x150' as const;
          },
        };
      },

      /** Base URL for Lleida eKYC document selection. */
      get LLEIDA_EKYC_BASE_URL() {
        return readEnv('NX_PUBLIC_SERVICE_LLEIDA_EKYC_BASE_URL');
      },

      /**
       * Nuvara / Inversis external account-opening entry URL.
       *
       * @remarks
       * Fixed endpoint; consumed by onboarding flows that deep-link users into
       * external validation steps.
       */
      get NUVARA_INVERSIS_ACCOUNT_OPENING_URL() {
        return 'https://admin.clickandautomate.eu/validateExternStart?tram=c_dWvxwI5U55Ae+v1/xAxzRg==%20%20%20' as const;
      },
    };
  },

  /**
   * AI HTTP integration secrets.
   *
   * @remarks
   * Env: `NX_PUBLIC_API_AI_BEARER_TOKEN`.
   */
  get AI() {
    return {
      /** Bearer token sent to AI HTTP APIs. */
      get API_BEARER_TOKEN() {
        return readEnv('NX_PUBLIC_API_AI_BEARER_TOKEN');
      },
    };
  },

  /**
   * Feature flags and tunable limits.
   *
   * @remarks
   * Variables: `NX_PUBLIC_FEATURE_*`.
   */
  get FEATURES() {
    return {
      /**
       * When `true`, subscriptions-related UI and API usage are enabled.
       *
       * @remarks
       * Env: `NX_PUBLIC_FEATURE_SUBSCRIPTIONS_ENABLED`.
       */
      get SUBSCRIPTIONS_ENABLED() {
        return readEnv('NX_PUBLIC_FEATURE_SUBSCRIPTIONS_ENABLED', 'boolean');
      },
      /**
       * Maximum equities count for AI builder flows (citec-app).
       *
       * @remarks
       * Env: `NX_PUBLIC_FEATURE_AI_MAX_STOCKS`.
       */
      get AI_MAX_STOCKS() {
        return readEnv('NX_PUBLIC_FEATURE_AI_MAX_STOCKS', 'number');
      },
    };
  },

  /**
   * Logging verbosity for client diagnostics.
   *
   * @remarks
   * Env: `NX_PUBLIC_LOG_VERBOSE`.
   */
  get LOGGING() {
    return {
      /** When `true`, verbose console logging is enabled. */
      get VERBOSE() {
        return readEnv('NX_PUBLIC_LOG_VERBOSE', 'boolean');
      },
    };
  },

  /**
   * Node execution mode (`development` | `test` | `production`).
   *
   * @remarks
   * Env: `NODE_ENV`. Must match {@link NodeEnv}.
   */
  get NODE_ENV() {
    return process.env['NODE_ENV'] as NodeEnv;
  },

  /**
   * Public build / deployment tier for feature gating (`local` | `development` |
   * `production`).
   *
   * @remarks
   * Env: `NX_PUBLIC_BUILD_ENV`. Must match {@link BuildEnv}.
   */
  get BUILD_ENV() {
    return readEnv('NX_PUBLIC_BUILD_ENV', { enum: Object.values(BuildEnv) });
  },
};

/**
 * `true` when {@link environment.NODE_ENV} is {@link NodeEnv.DEVELOPMENT}.
 *
 * @remarks
 * Evaluated once at module load. For live reads, compare `environment.NODE_ENV`
 * directly.
 */
export const isDevelopment = environment.NODE_ENV === NodeEnv.DEVELOPMENT;

/**
 * `true` when {@link environment.NODE_ENV} is {@link NodeEnv.TEST}.
 *
 * @remarks
 * Evaluated once at module load.
 */
export const isTest = environment.NODE_ENV === NodeEnv.TEST;

/**
 * `true` when {@link environment.NODE_ENV} is {@link NodeEnv.PRODUCTION}.
 *
 * @remarks
 * Evaluated once at module load.
 */
export const isProduction = environment.NODE_ENV === NodeEnv.PRODUCTION;

/**
 * `true` when {@link environment.BUILD_ENV} is {@link BuildEnv.LOCAL}.
 *
 * @remarks
 * Evaluated once at module load.
 */
export const isBuildLocal = environment.BUILD_ENV === BuildEnv.LOCAL;

/**
 * `true` when {@link environment.BUILD_ENV} is {@link BuildEnv.DEVELOPMENT}.
 *
 * @remarks
 * Evaluated once at module load.
 */
export const isBuildDevelopment = environment.BUILD_ENV === BuildEnv.DEVELOPMENT;

/**
 * `true` when {@link environment.BUILD_ENV} is {@link BuildEnv.PRODUCTION}.
 *
 * @remarks
 * Evaluated once at module load.
 */
export const isBuildProduction = environment.BUILD_ENV === BuildEnv.PRODUCTION;

/**
 * Returns the current application name from the runtime environment.
 *
 * @remarks
 * Re-evaluated on each call so that tests can mutate `process.env` without
 * reloading the module.
 */
export const getAppName = (): AppName =>
  environment.APP.NAME as string as unknown as AppName;

/**
 * Generates boolean flags for each {@link AppName} member keyed as `is<Member>App`
 * (e.g. `isAdvisorApp`, `isClientApp`).
 *
 * @internal Used to derive the exported per-app flags below.
 */
export const generateAppUtils = () => {
  const apps = Object.entries(AppName) as Array<[keyof typeof AppName, AppName]>;

  return Object.fromEntries<any>(
    apps.map(
      ([name, value]) => [`is${name}App` as const, value === getAppName()] as const,
    ),
  ) as Readonly<Record<`is${keyof typeof AppName}App`, boolean>>;
};

/**
 * `true` when the running app is the advisor application.
 *
 * @remarks
 * Evaluated once at module load. Compares {@link environment.APP.NAME} to
 * {@link AppName.Advisor}.
 */
export const isAdvisorApp = environment.APP.NAME === AppName.Advisor;

/**
 * `true` when the running app is the client application.
 *
 * @remarks
 * Evaluated once at module load. Compares {@link environment.APP.NAME} to
 * {@link AppName.Client}.
 */
export const isClientApp = environment.APP.NAME === AppName.Client;

/**
 * Returns `true` when the current app is included in `apps`.
 *
 * @param apps - Array of {@link AppName} values to match against.
 */
export const isAnyApp = (apps: AppName[]) => apps.includes(getAppName());

/**
 * Selects a value based on the current running app.
 *
 * @example
 *
 * ```ts
 * const timeout = appSelect({ advisor: 30_000, client: 60_000 });
 * ```
 *
 * @template T - Type of the values being selected.
 * @param options - Object with a key per app (`advisor`, `client`) and an optional
 *   `test` fallback used when running under Jest.
 * @throws {RangeError} When the current app name is not recognised.
 */
export const appSelect = <T>({
  advisor,
  client,
  test,
}: Record<Lowercase<keyof typeof AppName>, T> & { test?: T }): T => {
  switch (getAppName()) {
    case AppName.Advisor:
      return advisor;
    case AppName.Client:
      return client;
    default:
      if (test !== undefined && isTest) return test;
      throw new RangeError(`Invalid app name: ${getAppName()}`);
  }
};

/**
 * Returns `true` when the current {@link BuildEnv} is included in `envs`.
 *
 * @param envs - Array of {@link BuildEnv} values to match against.
 */
export const isAnyBuildEnv = (envs: BuildEnv[]) =>
  envs.includes(environment.BUILD_ENV as BuildEnv);

/**
 * Selects a value based on the current {@link BuildEnv}.
 *
 * @example
 *
 * ```ts
 * const apiUrl = buildEnvSelect({
 *   local: 'http://localhost:3000',
 *   development: 'https://api.dev.example.com',
 *   production: 'https://api.example.com',
 * });
 * ```
 *
 * @template T - Type of the values being selected.
 * @throws {RangeError} When the current build env is not recognised.
 */
export const buildEnvSelect = <T>({
  local,
  development,
  production,
}: Record<Lowercase<keyof typeof BuildEnv>, T>): T => {
  switch (environment.BUILD_ENV) {
    case BuildEnv.LOCAL:
      return local;
    case BuildEnv.DEVELOPMENT:
      return development;
    case BuildEnv.PRODUCTION:
      return production;
    default:
      throw new RangeError(`Invalid build env: ${environment.BUILD_ENV}`);
  }
};

/**
 * Returns `true` when the current {@link NodeEnv} is included in `envs`.
 *
 * @param envs - Array of {@link NodeEnv} values to match against.
 */
export const isAnyNodeEnv = (envs: NodeEnv[]) =>
  envs.includes(environment.NODE_ENV as NodeEnv);

/**
 * Selects a value based on the current {@link NodeEnv}.
 *
 * @example
 *
 * ```ts
 * const logLevel = nodeEnvSelect({
 *   development: 'debug',
 *   test: 'silent',
 *   production: 'error',
 * });
 * ```
 *
 * @template T - Type of the values being selected.
 * @throws {RangeError} When the current node env is not recognised.
 */
export const nodeEnvSelect = <T>({
  development,
  test,
  production,
}: Record<Lowercase<keyof typeof NodeEnv>, T>): T => {
  switch (environment.NODE_ENV) {
    case NodeEnv.DEVELOPMENT:
      return development;
    case NodeEnv.TEST:
      return test;
    case NodeEnv.PRODUCTION:
      return production;
    default:
      throw new RangeError(`Invalid node env: ${environment.NODE_ENV}`);
  }
};
